import React, { useMemo } from 'react'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import { history } from '../store'
import { drawReportHeader, drawSectionTitle, drawTable, PDF_MARGIN_X } from '../utils/pdfReport'

// ─── Battery thresholds ──────────────────────────────────────────────────────
// These mirror the "battery view" grid (MatrixConstructorBattery):
//   configured & Bateria < 4400        → red cross (#FL)  → "Sin batería"
//   configured & 4400 <= Bateria < 5800 → orange (#RK)     → "Poca batería"
//   configured & Bateria >= 5800        → green  (#AV)     → OK
//   not configured                      → black  (#Null)   → "Color Negro"
const MIN_BATTERY = 4400
const MAX_BATTERY = 6500
const LOW_BATTERY = 5800

// Fault code → Spanish label (same wording as the server PDF report)
const FAULT_LABELS = {
  BROKEN_LOCK: 'Pincho roto',
  GREEN_BLOCK: 'Pin verde no abre',
  RED_BLOCK: 'Pin rojo no abre',
  PINPAD_BLOCK: 'Teclado no enciende',
  PINPAD_ACTIVE: 'Teclado encendido',
  DOOR_BLOCK: 'Puerta no cierra',
}

function batteryPercent(bateria) {
  const pct = ((bateria - MIN_BATTERY) / (MAX_BATTERY - MIN_BATTERY)) * 100
  return Math.max(0, Math.min(100, Math.round(pct)))
}

function customerOf(machine) {
  return machine.Customer && machine.Customer.Fullname ? machine.Customer.Fullname : ''
}

/**
 * Lockers a machine has in service. LastIndex is the machine's locker count;
 * only lockers below it are drawn in the grid. Older machines may not carry it,
 * so fall back to the matrix length — buildFaultAnalysis uses the same fallback,
 * which keeps the incidence count from ever exceeding the total.
 */
function lockersInMachine(matrix, machine) {
  return (
    Number(machine.LastIndex) || (matrix && matrix.Locker ? matrix.Locker.length : 0)
  )
}

/**
 * Build the fault analysis rows for a customer, or for every customer when
 * customerName is null/undefined. Each row = one locker with a fault, black
 * (unconfigured) door, or battery issue.
 */
export function buildFaultAnalysis(matrixs, machines, customerName) {
  const rows = []
  const everyCustomer = customerName == null

  ;(matrixs || []).forEach((matrix) => {
    const machine = (machines || []).find((m) => m.Serial === matrix.Serial)
    if (!machine) return

    const name = customerOf(machine)
    if (!everyCustomer && name !== customerName) return

    const firstIndex = Number(machine.FirstIndex) || 0
    const lastIndex = lockersInMachine(matrix, machine)

    ;(matrix.Locker || []).forEach((lock, index) => {
      // Only lockers within the machine's active range are drawn in the grid
      if (index >= lastIndex) return

      const taquilla = index + firstIndex
      const configured = !!lock.Configured
      const hasFault = lock.FaultCode !== undefined && lock.Status === 'FL'
      const bateria = Number(lock.Bateria)
      const hasReading = Number.isFinite(bateria)

      // Battery classification only applies to configured lockers (as in the grid)
      let batteryState = null // 'SIN' | 'POCA' | 'OK'
      if (configured && hasReading) {
        if (bateria < MIN_BATTERY) batteryState = 'SIN'
        else if (bateria < LOW_BATTERY) batteryState = 'POCA'
        else batteryState = 'OK'
      }
      const pct = configured && hasReading ? batteryPercent(bateria) : null
      const batteryLabel =
        batteryState === 'SIN'
          ? 'Sin batería'
          : batteryState === 'POCA'
          ? 'Poca batería'
          : ''

      // Not configured → black door
      if (!configured) {
        rows.push({
          customer: name,
          serial: machine.Serial,
          taquilla,
          kind: 'NEGRO',
          averia: 'Color Negro',
          batteryLabel: '',
          pct: null,
        })
        return
      }

      if (hasFault) {
        // Fault (optionally with a co-occurring battery issue + %)
        rows.push({
          customer: name,
          serial: machine.Serial,
          taquilla,
          kind: 'AVERIA',
          averia: FAULT_LABELS[lock.FaultCode] || lock.FaultCode,
          batteryLabel,
          pct,
        })
      } else if (batteryState === 'SIN') {
        // No fault, but no battery → its own entry
        rows.push({
          customer: name,
          serial: machine.Serial,
          taquilla,
          kind: 'SIN',
          averia: 'Sin batería',
          batteryLabel: '',
          pct,
        })
      } else if (batteryState === 'POCA') {
        // No fault, but low battery → its own entry
        rows.push({
          customer: name,
          serial: machine.Serial,
          taquilla,
          kind: 'POCA',
          averia: 'Poca batería',
          batteryLabel: '',
          pct,
        })
      }
      // else: configured + healthy + no fault → nothing to report
    })
  })

  // Always sort by customer → locker number → machine serial (in that order).
  rows.sort(
    (a, b) =>
      (a.customer || '').localeCompare(b.customer || '', 'es', { sensitivity: 'base' }) ||
      (a.taquilla - b.taquilla) ||
      (a.serial || '').localeCompare(b.serial || '', undefined, { numeric: true, sensitivity: 'base' })
  )
  return rows
}

/**
 * Total lockers a customer has in service = Σ LastIndex over all its machines.
 * Machines with no matrix still count: their lockers exist, we just have no
 * reading for them.
 */
function countCustomerLockers(matrixs, machines, customerName) {
  return (machines || []).reduce((acc, machine) => {
    if (customerName != null && customerOf(machine) !== customerName) return acc
    const matrix = (matrixs || []).find((m) => m.Serial === machine.Serial)
    return acc + lockersInMachine(matrix, machine)
  }, 0)
}

/**
 * Incidence kinds that put a locker out of service. 'POCA' is deliberately
 * absent: a low battery only flags the locker for topping up, it still opens
 * for the customer. So this count is always ≤ the PDF's TOTAL INCIDENCIAS.
 */
const UNAVAILABLE_KINDS = ['AVERIA', 'NEGRO', 'SIN']

function countUnavailable(rows) {
  return rows.filter((r) => UNAVAILABLE_KINDS.indexOf(r.kind) !== -1).length
}

function availability(total, unavailable) {
  const available = Math.max(0, total - unavailable)
  return {
    total,
    unavailable,
    available,
    // A percentage of no lockers would be meaningless, so leave it unset.
    pct: total > 0 ? (available / total) * 100 : null,
  }
}

/**
 * Locker availability for one customer. Pass `rows` when the analysis has
 * already been built to save rebuilding it.
 */
export function buildAvailability(matrixs, machines, customerName, rows) {
  return availability(
    countCustomerLockers(matrixs, machines, customerName),
    countUnavailable(rows || buildFaultAnalysis(matrixs, machines, customerName))
  )
}

/**
 * Availability grouped in a single pass — the lists need every group at once,
 * and doing it per group would rescan the matrixs once per card on every
 * render. `machineKey` buckets a machine, `rowKey` buckets an analysis row.
 */
function buildAvailabilityBy(matrixs, machines, machineKey, rowKey) {
  const matrixBySerial = new Map((matrixs || []).map((m) => [m.Serial, m]))

  const totals = new Map()
  ;(machines || []).forEach((machine) => {
    const key = machineKey(machine)
    const lockers = lockersInMachine(matrixBySerial.get(machine.Serial), machine)
    totals.set(key, (totals.get(key) || 0) + lockers)
  })

  const unavailable = new Map()
  buildFaultAnalysis(matrixs, machines, null)
    .filter((r) => UNAVAILABLE_KINDS.indexOf(r.kind) !== -1)
    .forEach((r) => {
      const key = rowKey(r)
      unavailable.set(key, (unavailable.get(key) || 0) + 1)
    })

  const grouped = new Map()
  totals.forEach((total, key) =>
    grouped.set(key, availability(total, unavailable.get(key) || 0))
  )
  return grouped
}

/** Availability keyed by customer name. */
export function buildAvailabilityByCustomer(matrixs, machines) {
  return buildAvailabilityBy(matrixs, machines, customerOf, (r) => r.customer)
}

/** Availability keyed by machine serial. */
export function buildAvailabilityByMachine(matrixs, machines) {
  return buildAvailabilityBy(matrixs, machines, (m) => m.Serial, (r) => r.serial)
}

function safeCustomer(customer) {
  return (customer || 'cliente').replace(/[^a-z0-9]+/gi, '_')
}

/** Download the customer's fault analysis as an Excel file. */
export function downloadFaultAnalysisExcel(matrixs, machines, customer) {
  const rows = buildFaultAnalysis(matrixs, machines, customer)
  const data = rows.map((r) => ({
    Máquina: r.serial,
    Taquilla: r.taquilla,
    Avería: r.averia,
    Batería: r.batteryLabel,
    '% Batería': r.pct != null ? `${r.pct}%` : '',
  }))
  const ws = XLSX.utils.json_to_sheet(data, {
    header: ['Máquina', 'Taquilla', 'Avería', 'Batería', '% Batería'],
  })
  ws['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 22 }, { wch: 16 }, { wch: 12 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Análisis')
  XLSX.writeFile(
    wb,
    `analisis-averias-${safeCustomer(customer)}-${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`
  )
}

// Non-fault incidence kinds, in a fixed reading order. Faults are not listed
// here: they get one counter row per fault type, as in the repair report.
const OTHER_KIND_LABELS = [
  ['NEGRO', 'Color Negro'],
  ['POCA', 'Poca batería'],
  ['SIN', 'Sin batería'],
]

/** Download the customer's fault analysis as a PDF file (same content as Excel). */
export function downloadFaultAnalysisPDF(matrixs, machines, customer) {
  const rows = buildFaultAnalysis(matrixs, machines, customer)
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const marginX = PDF_MARGIN_X
  let y = drawReportHeader(doc, 'Informe de Averías')

  const now = new Date()
  const fecha = `${now.toLocaleDateString('es-ES')} ${now.toLocaleTimeString(
    'es-ES',
    { hour: '2-digit', minute: '2-digit', hour12: false }
  )}`
  doc.text(`Cliente: ${customer || '-'}`, marginX, y)
  y += 14
  doc.text(`Fecha: ${fecha}`, marginX, y)
  y += 26

  // Locker availability. Drawn even with no incidences, where it reads 100 %.
  const total = rows.length
  const av = buildAvailability(matrixs, machines, customer, rows)
  y = drawSectionTitle(doc, y, 'Disponibilidad de taquillas')
  y = drawTable(
    doc,
    y,
    [
      { title: 'Concepto', width: 355, align: 'left' },
      { title: 'Valor', width: 160, align: 'right' },
    ],
    [
      { cells: ['Taquillas totales', String(av.total)] },
      { cells: ['Taquillas no disponibles', String(av.unavailable)] },
      { cells: ['Taquillas disponibles', String(av.available)] },
      {
        cells: [
          'DISPONIBILIDAD',
          av.pct != null ? `${av.pct.toFixed(1)} %` : '—',
        ],
        bold: true,
      },
    ],
    { fontSize: 9 }
  )
  y += 12

  // The listing below counts low battery as an incidence, so spell out why the
  // two figures differ rather than letting it read as an arithmetic error.
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(8)
  doc.text(
    'No disponibles = averías, taquillas en negro y sin batería. Las taquillas con poca batería siguen operativas y no restan disponibilidad.',
    marginX,
    y
  )
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  y += 24

  // Incidence counter
  if (total > 0) {
    y = drawSectionTitle(doc, y, 'Resumen de incidencias')

    const sumCols = [
      { title: 'Incidencia', width: 305, align: 'left' },
      { title: 'Nº', width: 105, align: 'right' },
      { title: '% del total', width: 105, align: 'right' },
    ]
    const sumRow = (label, count) => ({
      cells: [label, String(count), `${((count / total) * 100).toFixed(1)} %`],
    })

    // One row per fault type, most frequent first
    const faultCounts = new Map()
    rows
      .filter((r) => r.kind === 'AVERIA')
      .forEach((r) => faultCounts.set(r.averia, (faultCounts.get(r.averia) || 0) + 1))

    const sumRows = Array.from(faultCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => sumRow(label, count))

    // …then the non-fault incidences, always shown even at zero
    OTHER_KIND_LABELS.forEach(([kind, label]) =>
      sumRows.push(sumRow(label, rows.filter((r) => r.kind === kind).length))
    )

    sumRows.push({
      cells: ['TOTAL INCIDENCIAS', String(total), '100.0 %'],
      bold: true,
    })

    y = drawTable(doc, y, sumCols, sumRows, { fontSize: 9 })
    y += 26
  }

  // Listing
  if (y > doc.internal.pageSize.getHeight() - 90) {
    doc.addPage()
    y = 40
  }
  y = drawSectionTitle(doc, y, 'Listado de averías')

  if (total === 0) {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.text('Sin incidencias.', marginX, y + 12)
  } else {
    const listCols = [
      { title: 'Máquina', width: 100, align: 'left' },
      { title: 'Taquilla', width: 60, align: 'right' },
      { title: 'Avería', width: 175, align: 'left' },
      { title: 'Batería', width: 100, align: 'left' },
      { title: '% Batería', width: 80, align: 'right' },
    ]
    const listRows = rows.map((r) => ({
      cells: [
        r.serial || '',
        String(r.taquilla),
        r.averia || '',
        r.batteryLabel || '—',
        r.pct != null ? `${r.pct} %` : '—',
      ],
    }))

    y = drawTable(doc, y, listCols, listRows, { fontSize: 9 })
  }

  doc.save(
    `INFORME_AVERIAS_${safeCustomer(customer)}_${now.toISOString().slice(0, 10)}.pdf`
  )
}

// ─── Modal ───────────────────────────────────────────────────────────────────

const FaultsAnalysisModal = ({ open, onClose, matrixs, machines, customer }) => {
  const rows = useMemo(
    () => (open ? buildFaultAnalysis(matrixs, machines, customer) : []),
    [open, matrixs, machines, customer]
  )

  if (!open) return null

  const groups = {
    AVERIA: rows.filter((r) => r.kind === 'AVERIA'),
    NEGRO: rows.filter((r) => r.kind === 'NEGRO'),
    POCA: rows.filter((r) => r.kind === 'POCA'),
    SIN: rows.filter((r) => r.kind === 'SIN'),
  }

  const renderItem = (r, i) => (
    <button
      key={`${r.serial}-${r.taquilla}-${i}`}
      type="button"
      className="ns-analysis-item"
      onClick={() => history.push(`/machines/${r.serial}`)}
      title="Abrir máquina"
    >
      <div className="ns-analysis-item__main">
        <div className="ns-analysis-item__serial">
          {r.serial} · Taquilla {r.taquilla}
        </div>
        <div className="ns-analysis-item__customer">
          {r.averia}
          {r.batteryLabel ? ` · ${r.batteryLabel}` : ''}
        </div>
      </div>
      <div className="ns-analysis-item__meta">
        {r.pct != null && (
          <span
            style={{
              display: 'inline-block',
              borderRadius: 999,
              padding: '2px 8px',
              fontWeight: 700,
              fontSize: 11.5,
              background: r.pct < 33 ? '#fee2e2' : r.pct < 67 ? '#ffedd5' : '#dcfce7',
              color: r.pct < 33 ? '#b91c1c' : r.pct < 67 ? '#c2410c' : '#166534',
            }}
          >
            {r.pct}% batería
          </span>
        )}
      </div>
    </button>
  )

  const renderSection = (title, list, headBg, headColor, countBg) => (
    <div className="ns-analysis-section">
      <div
        className="ns-analysis-section__head"
        style={{ background: headBg, color: headColor }}
      >
        <span>{title}</span>
        <span
          className="ns-analysis-count"
          style={{ background: countBg, color: '#fff' }}
        >
          {list.length}
        </span>
      </div>
      <div className="ns-analysis-list">
        {list.length === 0 ? (
          <div className="ns-analysis-empty">Ninguna ✓</div>
        ) : (
          list.map(renderItem)
        )}
      </div>
    </div>
  )

  const total = rows.length

  return (
    <div className="ns-analysis-overlay" onClick={onClose}>
      <div className="ns-analysis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ns-analysis-header">
          <div>
            <h2>Análisis de averías — {customer || 'Cliente'}</h2>
            <div className="ns-analysis-sub">
              Averías, taquillas en negro y estado de batería · {total}{' '}
              {total === 1 ? 'incidencia' : 'incidencias'}
            </div>
          </div>
          <div className="ns-analysis-actions">
            <button
              className="nightscan-csv-btn"
              onClick={() => downloadFaultAnalysisExcel(matrixs, machines, customer)}
              disabled={total === 0}
              title="Descargar análisis en Excel"
            >
              <i className="fas fa-file-excel"></i>
              Excel
            </button>
            <button
              className="nightscan-csv-btn"
              onClick={() => downloadFaultAnalysisPDF(matrixs, machines, customer)}
              disabled={total === 0}
              title="Descargar análisis en PDF"
              style={{ background: 'linear-gradient(135deg, #b91c1c, #ef4444)' }}
            >
              <i className="fas fa-file-pdf"></i>
              PDF
            </button>
            <button
              className="ns-analysis-close"
              onClick={onClose}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="ns-analysis-body">
          {total === 0 ? (
            <div className="ns-analysis-empty" style={{ padding: 32 }}>
              Este cliente no tiene averías, taquillas en negro ni incidencias de
              batería ✓
            </div>
          ) : (
            <>
              <div className="ns-analysis-grid">
                {renderSection(
                  'Averías',
                  groups.AVERIA,
                  '#fef2f2',
                  '#b91c1c',
                  '#b91c1c'
                )}
                {renderSection(
                  'Taquillas en negro',
                  groups.NEGRO,
                  '#f1f5f9',
                  '#0f172a',
                  '#0f172a'
                )}
              </div>
              <div className="ns-analysis-grid">
                {renderSection(
                  'Poca batería',
                  groups.POCA,
                  '#fff7ed',
                  '#c2410c',
                  '#c2410c'
                )}
                {renderSection(
                  'Sin batería',
                  groups.SIN,
                  '#fef2f2',
                  '#b91c1c',
                  '#b91c1c'
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default FaultsAnalysisModal
